import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../api/axiosInstance.js";
import Loader from "../components/Loader.jsx";
import { toast } from "react-toastify";
import { FaChevronDown, FaCopy, FaCheck, FaInfoCircle, FaSearch, FaUserShield, FaUnlink } from "react-icons/fa";
import ForceDisconnectDialog, { forceDisconnectOutcome } from "../components/abuse/ForceDisconnectDialog.jsx";

const TrialExploitationMonitor = () => {
  const token = useSelector((state) => state.user.token);
  // The backend enforces admin on its own; this only hides a button that
  // would answer 403 for the "user" dashboard role.
  const roles = useSelector((state) => state.user.roles);
  const isAdmin = Array.isArray(roles) && roles.includes("admin");
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("desc");
  const [brand, setBrand] = useState("");
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);
  
  // Track specifically which field of which user was copied
  const [copiedState, setCopiedState] = useState(null); // { id: userId, type: 'name' | 'email' }

  // { userId, name, email, accountId } of the card whose Force Disconnect was clicked
  const [forceTarget, setForceTarget] = useState(null);

  useEffect(() => {
    if (token) fetchExploitationLogs();
  }, [page, sort, brand, token]);

  const fetchExploitationLogs = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/admin/getabusereport", {
        params: { page, limit: 10, sort, brand },
      });
      setData(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch (err) {
      console.error("Failed to fetch exploitation logs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, userId, type) => {
    navigator.clipboard.writeText(text);
    setCopiedState({ id: userId, type });
    setTimeout(() => setCopiedState(null), 1200);
  };

  const closeForceDialog = useCallback(() => setForceTarget(null), []);

  const handleForceDisconnectDone = (result) => {
    const { kind, text } = forceDisconnectOutcome(result);
    if (kind === "success") toast.success(text);
    else toast.info(text);
    setForceTarget(null);
    fetchExploitationLogs();
  };

  const getStatusStyle = (count) => {
    if (count <= 1) return "border-l-emerald-500 bg-emerald-50/10";
    if (count <= 3) return "border-l-amber-500 bg-amber-50/10";
    return "border-l-rose-500 bg-rose-50/10";
  };

  if (loading) return <Loader />;

  return (
    <div className="w-full min-h-screen bg-[#fcfcfc] p-4 font-sans text-slate-800">
      <div className="w-full max-w-[1600px] mx-auto space-y-4">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center bg-white p-5 border border-slate-200 rounded-lg shadow-sm gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-slate-900 p-3 rounded-lg text-white">
              <FaUserShield size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight text-slate-900 uppercase">Trial Exploitation Monitor</h2>
                
                {/* Information Icon & Tooltip */}
                <div className="relative group cursor-help">
                  <FaInfoCircle className="text-slate-400 hover:text-slate-600 transition-colors" size={16} />
                  <div className="absolute left-0 top-8 w-80 p-4 bg-slate-900 text-white text-[11px] leading-relaxed rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 border border-slate-700">
                    <p className="font-bold text-amber-400 mb-2 uppercase tracking-wider underline">Anti-Exploit Protocol:</p>
                    <p className="mb-2 text-slate-200">Flags Hardware/Providers linked to multiple email registrations. This identifies users deleting accounts to cycle free trials on the same device.</p>
                    <p className="p-2 bg-slate-800 rounded border border-slate-700 text-slate-300 italic">
                      Workflow: Copy Email &rarr; Search User DB &rarr; Verify Trial Pattern &rarr; Suspend to Restrict.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Multi-Account Detection & Device ID Auditing</p>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex border border-slate-200 rounded-md overflow-hidden shadow-sm">
              <button
                onClick={() => { setSort("desc"); setPage(1); }}
                className={`px-4 py-2 text-[10px] font-black transition-colors ${sort === 'desc' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                MAX FLAGS
              </button>
              <button
                onClick={() => { setSort("asc"); setPage(1); }}
                className={`px-4 py-2 text-[10px] font-black border-l border-slate-200 transition-colors ${sort === 'asc' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                MIN FLAGS
              </button>
            </div>
            <select
              value={brand}
              onChange={(e) => { setBrand(e.target.value); setPage(1); }}
              className="px-3 py-2 text-[10px] font-black border border-slate-200 rounded-md bg-white outline-none focus:ring-1 focus:ring-slate-300 uppercase shadow-sm"
            >
              <option value="">ALL BRANDS</option>
              <option value="tapo">TAPO</option>
              <option value="honeywell">HONEYWELL</option>
            </select>
          </div>
        </div>

        {/* Audit List */}
        <div className="flex flex-col gap-2">
          {data.map((item) => {
            const isOpen = openRow === item._id;
            const styleClass = getStatusStyle(item.reportCount);

            return (
              <div 
                key={item._id} 
                className={`w-full bg-white border transition-all ${isOpen ? 'shadow-lg border-slate-400 scale-[1.002]' : 'border-slate-200 hover:border-slate-300 shadow-sm'}`}
              >
                {/* Main Row */}
                <div
                  onClick={() => setOpenRow(isOpen ? null : item._id)}
                  className={`flex items-center justify-between p-3 md:p-4 cursor-pointer border-l-[6px] ${styleClass}`}
                >
                  <div className="flex gap-10 md:gap-20 items-center">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">PROVIDER ID</span>
                      <span className="font-mono text-sm font-bold text-slate-700">{item.providerId}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Brand</span>
                      <span className="text-sm font-semibold capitalize">{item.brand}</span>
                    </div>
                    <div className="hidden lg:flex flex-col">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Recent Activity</span>
                      <span className="text-sm text-slate-600">{new Date(item.latestReportAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Account Count</span>
                      <span className={`text-lg font-black ${item.reportCount > 3 ? 'text-rose-600' : 'text-slate-800'}`}>
                        {item.reportCount}
                      </span>
                    </div>
                    <FaChevronDown className={`text-slate-400 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* User Detail Grid */}
                {isOpen && (
                  <div className="p-4 bg-slate-50 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-4 text-[10px] font-black text-slate-500 uppercase tracking-[0.15em]">
                      <FaSearch size={10} className="text-slate-400" /> Linked Accounts for Deletion/Creation Pattern
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {item.users.map((user) => (
                        <div
                          key={user.userId}
                          className="group relative flex items-center bg-white p-3 border border-slate-200 rounded shadow-sm hover:border-slate-900 transition-all overflow-hidden"
                        >
                          <div className="flex-1 truncate">
                            <p className="text-[13px] font-bold text-slate-800 truncate">{user.name}</p>
                            <p className="text-[11px] text-slate-500 truncate mb-1">{user.email}</p>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter italic border-t pt-1 border-slate-50">
                              Registered: {new Date(user.reportedAt).toLocaleDateString()}
                            </p>
                            {isAdmin && String(item.brand).toLowerCase() === "tapo" && (
                              <div className="mt-2 flex items-center gap-2">
                                {user.isLinked && (
                                  <span
                                    title="This account currently holds this Tapo connection"
                                    className="px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest rounded bg-emerald-100 text-emerald-800"
                                  >
                                    Linked
                                  </span>
                                )}
                                <button
                                  type="button"
                                  aria-label={`Force Disconnect ${user.email || user.name || user.userId}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setForceTarget({ userId: user.userId, name: user.name, email: user.email, accountId: item.providerId });
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 transition-colors"
                                >
                                  <FaUnlink size={9} /> Force Disconnect
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Dual Action Copy Strip */}
                          <div className="absolute right-0 top-0 bottom-0 w-12 flex flex-col bg-slate-900 text-white translate-x-full group-hover:translate-x-0 transition-transform duration-150 divide-y divide-slate-700/50">
                            
                            {/* Copy Name Section */}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(user.name, user.userId, 'name'); }}
                              className="flex-1 flex flex-col items-center justify-center hover:bg-slate-800 transition-colors"
                              title="Copy Name"
                            >
                              {copiedState?.id === user.userId && copiedState?.type === 'name' ? (
                                <FaCheck size={10} className="text-emerald-400" />
                              ) : (
                                <FaCopy size={10} className="text-slate-400" />
                              )}
                              <span className="text-[6px] mt-1 font-black tracking-widest uppercase">NAME</span>
                            </button>

                            {/* Copy Email Section */}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(user.email, user.userId, 'email'); }}
                              className="flex-1 flex flex-col items-center justify-center hover:bg-slate-800 transition-colors"
                              title="Copy Email"
                            >
                              {copiedState?.id === user.userId && copiedState?.type === 'email' ? (
                                <FaCheck size={10} className="text-emerald-400" />
                              ) : (
                                <FaCopy size={10} className="text-slate-400" />
                              )}
                              <span className="text-[6px] mt-1 font-black tracking-widest uppercase">EMAIL</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <ForceDisconnectDialog
          target={forceTarget}
          onCancel={closeForceDialog}
          onDone={handleForceDisconnectDone}
        />

        {/* Footer Pagination */}
        <div className="flex justify-between items-center p-3 bg-white border border-slate-200 rounded shadow-sm">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="text-[10px] font-black text-slate-500 hover:text-slate-900 disabled:opacity-20 transition-colors uppercase tracking-widest"
          >
            &larr; Previous Audit
          </button>
          
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Page <span className="text-slate-900">{pagination.page}</span> / <span className="text-slate-900">{pagination.totalPages}</span>
          </div>

          <button
            disabled={page === pagination.totalPages}
            onClick={() => setPage(p => p + 1)}
            className="text-[10px] font-black text-slate-500 hover:text-slate-900 disabled:opacity-20 transition-colors uppercase tracking-widest"
          >
            Next Audit &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExploitationMonitor;